import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@shared/services/api';
import { Card, Button } from '@shared/components/ui';
import { CreditCard, MapPin, Globe, ShieldCheck, Check, ShoppingCart, Tag } from 'lucide-react';
import { planApi } from '@features/billing/api/planApi';
import { type Plan } from '@features/billing/types/plan';

interface PromoCodeDetails {
  valid: boolean;
  code: string;
  description: string;
  discountType: string;
  discountAmount: number;
  originalAmount: number;
  finalAmount: number;
  display: string;
}

interface TaxDetails {
  country: string;
  region?: string;
  taxRate: number;
  taxAmount: number;
  taxName: string;
  totalWithTax: number;
}

// Brazil only - hardcoded for now as per business requirements
const COUNTRIES = [{ code: 'BR', translationKey: 'checkout.brazilCountryName', hasRegions: true }];

const BRAZIL_STATES = [
  { code: 'AC', name: 'Acre' },
  { code: 'AL', name: 'Alagoas' },
  { code: 'AP', name: 'Amapá' },
  { code: 'AM', name: 'Amazonas' },
  { code: 'BA', name: 'Bahia' },
  { code: 'CE', name: 'Ceará' },
  { code: 'DF', name: 'Distrito Federal' },
  { code: 'ES', name: 'Espírito Santo' },
  { code: 'GO', name: 'Goiás' },
  { code: 'MA', name: 'Maranhão' },
  { code: 'MT', name: 'Mato Grosso' },
  { code: 'MS', name: 'Mato Grosso do Sul' },
  { code: 'MG', name: 'Minas Gerais' },
  { code: 'PA', name: 'Pará' },
  { code: 'PB', name: 'Paraíba' },
  { code: 'PR', name: 'Paraná' },
  { code: 'PE', name: 'Pernambuco' },
  { code: 'PI', name: 'Piauí' },
  { code: 'RJ', name: 'Rio de Janeiro' },
  { code: 'RN', name: 'Rio Grande do Norte' },
  { code: 'RS', name: 'Rio Grande do Sul' },
  { code: 'RO', name: 'Rondônia' },
  { code: 'RR', name: 'Roraima' },
  { code: 'SC', name: 'Santa Catarina' },
  { code: 'SP', name: 'São Paulo' },
  { code: 'SE', name: 'Sergipe' },
  { code: 'TO', name: 'Tocantins' },
];

export default function Checkout() {
  const { t, i18n } = useTranslation('billing');
  const [searchParams] = useSearchParams();
  const planName = searchParams.get('plan') || '';

  // Plans state
  const [plans, setPlans] = useState<Plan[]>([]);
  const [planLoading, setPlanLoading] = useState(true);
  const [planAttempt, setPlanAttempt] = useState(0);
  const [planError, setPlanError] = useState<string | null>(null);

  const [promoCode, setPromoCode] = useState('');
  const [promoDetails, setPromoDetails] = useState<PromoCodeDetails | null>(null);
  const [validating, setValidating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);

  // Tax state - default to Brazil
  const [country, setCountry] = useState('BR');
  const [region, setRegion] = useState('');
  const [taxDetails, setTaxDetails] = useState<TaxDetails | null>(null);

  // Fetch plans from database
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setPlanLoading(true);
        setPlanError(null);
        const publicPlans = await planApi.getPublicPlans();
        setPlans(publicPlans);
      } catch (err) {
        console.error('Failed to fetch plans:', err);
        setPlanError(t('checkout.loadPlansError'));
      } finally {
        setPlanLoading(false);
      }
    };
    fetchPlans();
  }, [t, planAttempt]);

  // Find the selected plan from fetched plans
  const plan = plans.find((p) => p.name === planName);
  const planAmount = plan?.monthlyPrice || 0;
  const finalAmount = promoDetails ? promoDetails.finalAmount : planAmount;
  const discountAmount = promoDetails ? promoDetails.discountAmount : 0;

  const formatCheckoutPrice = (cents: number) =>
    new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language, {
      style: 'currency',
      currency: plan?.currency || 'BRL',
    }).format(cents / 100);
  const formatCheckoutPercent = (rate: number) =>
    new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language, {
      style: 'percent',
      maximumFractionDigits: 0,
    }).format(rate);

  // Calculate tax only for paid plans. Trial activation does not collect payment details.
  useEffect(() => {
    if (!plan || plan.monthlyPrice <= 0 || finalAmount <= 0 || !region) {
      setTaxDetails(null);
      return;
    }
    const calculateTax = async () => {
      setTaxDetails(null);
      try {
        const response = await api.post('/billing/calculate-tax', {
          country,
          region: region || undefined,
          amount: finalAmount,
        });
        setTaxDetails(response.data);
      } catch {
        // Ignore errors
      }
    };
    calculateTax();
  }, [country, region, finalAmount, plan]);

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoError(t('checkout.enterPromoCode'));
      return;
    }

    setValidating(true);
    setPromoError(null);
    setPromoDetails(null);

    try {
      const response = await api.post('/billing/validate-promo-code', {
        promoCode: promoCode.trim().toUpperCase(),
        planName: planName,
      });
      setPromoDetails(response.data);
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { error?: string } } }).response?.data
        ?.error;
      setPromoError(errorMessage || t('checkout.invalidPromoCode'));
    } finally {
      setValidating(false);
    }
  };

  const handleCheckout = async () => {
    setLoading(true);
    setError(null);

    try {
      // Check if this is a trial plan (free)
      if (plan?.monthlyPrice === 0) {
        // Activate trial directly
        await api.post('/billing/subscription/start-trial', {
          planName: planName,
          trialDays: 14,
        });

        // Redirect to success page
        window.location.href = `/billing/success?trial=true&plan=${planName}`;
        return;
      }

      // Regular paid plan - use Stripe checkout
      const response = await api.post('/billing/create-checkout-session', {
        planName: planName,
        promoCode: promoDetails ? promoDetails.code : undefined,
        successUrl: `${window.location.origin}/billing/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/pricing`,
      });

      if (response.data.sessionUrl) window.location.href = response.data.sessionUrl;
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { error?: string } } }).response?.data
        ?.error;
      // errorMessage may be a backend i18n key (e.g. "billing.trial_already_used")
      // or an already-translated string — try t() first, fall back to raw text
      const displayError =
        errorMessage && errorMessage.includes('.') && !errorMessage.includes(' ')
          ? t(errorMessage) || errorMessage
          : errorMessage || t('checkout.checkoutError');
      setError(displayError);
    } finally {
      setLoading(false);
    }
  };

  if (planLoading) {
    return (
      <>
        <div className="max-w-4xl mx-auto flex items-center justify-center py-20">
          <div
            role="status"
            aria-label={t('checkout.loadingPlans')}
            className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"
          />
        </div>
      </>
    );
  }

  if (planError || !plan) {
    return (
      <>
        <div role="alert" className="max-w-4xl mx-auto py-20 text-center">
          <h1 className="text-2xl font-bold text-state-danger-light dark:text-state-danger-dark mb-4">
            {planError || t(planName ? 'checkout.planNotFound' : 'checkout.planRequiredTitle')}
          </h1>
          <p className="text-text-muted-light dark:text-text-muted-dark mb-6">
            {!planError &&
              (planName
                ? t('checkout.planNotAvailable', { plan: planName })
                : t('checkout.planRequiredMessage'))}
          </p>
          {planError && (
            <Button className="mb-3 mx-2" onClick={() => setPlanAttempt((value) => value + 1)}>
              {t('common:actions.retry')}
            </Button>
          )}
          <Button onClick={() => (window.location.href = '/pricing')}>
            {t('checkout.viewAvailablePlans')}
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-700">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
            {plan.monthlyPrice === 0 ? t('checkout.trialTitle') : t('checkout.title')}
          </h1>
          <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-2">
            {plan.monthlyPrice === 0 ? t('checkout.trialSubtitle') : t('checkout.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Billing Info */}
          {plan.monthlyPrice > 0 && (
            <Card className="p-6 h-fit">
              <h2 className="text-lg font-black text-slate-900 dark:text-white mb-6 flex items-center gap-2">
                <MapPin className="text-brand-600 dark:text-brand-400" />{' '}
                {t('checkout.billingAddress')}
              </h2>

              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="checkout-country"
                    className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
                  >
                    {t('checkout.country')}
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle-light dark:text-text-subtle-dark w-4 h-4" />
                    <select
                      id="checkout-country"
                      aria-describedby="checkout-country-availability"
                      value={country}
                      onChange={(e) => {
                        setCountry(e.target.value);
                        setRegion('');
                      }}
                      disabled
                      className="w-full pl-10 pr-4 py-3 bg-surface-2-light dark:bg-surface-2-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none appearance-none cursor-not-allowed"
                    >
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {t(c.translationKey)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p
                    id="checkout-country-availability"
                    className="text-xs text-text-subtle-light dark:text-text-subtle-dark mt-1"
                  >
                    {t('checkout.brazilOnly')}
                  </p>
                </div>

                {country === 'BR' && (
                  <div>
                    <label
                      htmlFor="checkout-region"
                      className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
                    >
                      {t('checkout.state')}
                    </label>
                    <select
                      id="checkout-region"
                      value={region}
                      onChange={(e) => {
                        setRegion(e.target.value);
                        setError(null);
                      }}
                      className="min-h-11 w-full px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                    >
                      <option value="">{t('placeholders.select_state')}</option>
                      {BRAZIL_STATES.map((s) => (
                        <option key={s.code} value={s.code}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="mt-8 p-4 bg-state-info-bg-light dark:bg-state-info-bg-dark border border-state-info-light/20 dark:border-state-info-dark/20 rounded-xl flex items-start gap-3">
                <ShieldCheck
                  className="text-state-info-light dark:text-state-info-dark shrink-0 mt-0.5"
                  size={20}
                />
                <div className="text-xs">
                  <p className="font-bold text-state-info-light dark:text-state-info-dark mb-1">
                    {t('checkout.secureTransaction')}
                  </p>
                  <p className="text-state-info-light dark:text-state-info-dark">
                    {t('checkout.secureDescription')}
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Order Summary */}
          <Card
            className={`p-6 bg-surface-2-light dark:bg-surface-2-dark border-surface-border-light dark:border-surface-border-dark ${plan.monthlyPrice === 0 ? 'md:col-span-2' : ''}`}
          >
            <h2 className="text-lg font-black text-slate-900 dark:text-white mb-6 flex items-center gap-2">
              <ShoppingCart className="text-brand-600 dark:text-brand-400" />{' '}
              {t('checkout.orderSummary')}
            </h2>

            <div className="space-y-4 mb-6">
              <div className="flex justify-between items-center pb-4 border-b border-surface-border-light dark:border-surface-border-dark">
                <div>
                  <p className="font-bold text-slate-900 dark:text-white text-lg">
                    {t(`pricing.planNames.${plan.name}`, {
                      defaultValue: plan.displayName || plan.name,
                    })}
                  </p>
                  <p className="text-xs text-text-muted-light dark:text-text-muted-dark font-bold uppercase tracking-wider">
                    {plan.monthlyPrice === 0
                      ? t('checkout.trialDuration')
                      : t('checkout.monthlyBilling')}
                  </p>
                </div>
                <p className="font-black text-xl text-slate-900 dark:text-white">
                  {formatCheckoutPrice(planAmount)}
                </p>
              </div>

              {/* Promo Code */}
              {plan.monthlyPrice > 0 && (
                <div className="space-y-2">
                  {!promoDetails ? (
                    <>
                      <label
                        htmlFor="checkout-promo-code"
                        className="block text-xs font-bold text-text-muted-light dark:text-text-muted-dark"
                      >
                        {t('checkout.promoCode')}
                      </label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Tag
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle-light dark:text-text-subtle-dark w-4 h-4"
                            aria-hidden="true"
                          />
                          <input
                            id="checkout-promo-code"
                            type="text"
                            autoComplete="off"
                            placeholder={t('placeholders.promo_code')}
                            value={promoCode}
                            onChange={(e) => {
                              setPromoCode(e.target.value.toUpperCase());
                              setPromoError(null);
                            }}
                            className="min-h-11 w-full pl-10 pr-4 py-2 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-lg text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                          />
                        </div>
                        <Button
                          size="sm"
                          onClick={handleApplyPromoCode}
                          isLoading={validating}
                          disabled={!promoCode.trim()}
                          className="min-h-11"
                        >
                          {t('checkout.apply')}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between items-center p-3 bg-state-ok-bg-light dark:bg-state-ok-bg-dark border border-state-ok-light/20 dark:border-state-ok-dark/20 rounded-lg">
                      <div className="flex items-center gap-2 text-state-ok-light dark:text-state-ok-dark">
                        <Check size={16} />
                        <span className="font-bold text-sm">
                          {promoDetails.code} {t('checkout.applied')}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setPromoDetails(null);
                          setPromoCode('');
                        }}
                        className="min-h-11 rounded px-3 text-xs font-bold text-state-danger-light dark:text-state-danger-dark hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                      >
                        {t('checkout.remove')}
                      </button>
                    </div>
                  )}
                  {promoError && (
                    <p
                      role="alert"
                      className="text-xs font-bold text-state-danger-light dark:text-state-danger-dark ml-1"
                    >
                      {promoError}
                    </p>
                  )}
                </div>
              )}

              {discountAmount > 0 && (
                <div className="flex justify-between text-state-ok-light dark:text-state-ok-dark font-bold text-sm">
                  <span>{t('checkout.discount')}</span>
                  <span>-{formatCheckoutPrice(discountAmount)}</span>
                </div>
              )}

              {taxDetails && taxDetails.taxAmount > 0 && (
                <div className="flex justify-between text-text-muted-light dark:text-text-muted-dark font-medium text-sm">
                  <span>
                    {taxDetails.taxName} ({formatCheckoutPercent(taxDetails.taxRate)})
                  </span>
                  <span>{formatCheckoutPrice(taxDetails.taxAmount)}</span>
                </div>
              )}

              <div className="flex justify-between items-center pt-4 border-t border-surface-border-light dark:border-surface-border-dark">
                <span className="font-black text-slate-900 dark:text-white text-lg">
                  {plan.monthlyPrice === 0 ? t('checkout.totalDueToday') : t('checkout.totalDue')}
                </span>
                <span className="font-black text-2xl text-brand-600 dark:text-brand-400">
                  {formatCheckoutPrice(taxDetails?.totalWithTax ?? finalAmount)}
                </span>
              </div>
            </div>

            {error && (
              <div
                role="alert"
                className="p-3 mb-4 bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-lg text-state-danger-light dark:text-state-danger-dark text-sm font-bold text-center"
              >
                {error}
              </div>
            )}

            <Button
              onClick={handleCheckout}
              isLoading={loading}
              className="w-full h-12 text-lg shadow-lg shadow-brand-200/50"
            >
              {plan.monthlyPrice === 0 ? t('checkout.startTrial') : t('checkout.paySecurely')}
            </Button>

            {plan.monthlyPrice > 0 && (
              <p className="text-center text-xs text-text-subtle-light dark:text-text-subtle-dark font-bold mt-4 flex items-center justify-center gap-1">
                <CreditCard size={12} aria-hidden="true" /> {t('checkout.poweredByStripe')}
              </p>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}
